import { Workflow, TargetScope } from "@/types/Workflow";
import { Field } from "@/types/Field";
import { DataType } from "@/types/enums/DataType";
import { ActionType } from "@/types/enums/ActionType";
import { EmailTrigger } from "@/types/enums/EmailTrigger";
import { ConditionGroup } from "@/types/EventRule";
import { evaluateConditionGroup } from "./evaluate-condition";
import { readGuestUpdates } from "./guestUpdates";
import { readTargetScope } from "./target-scope";
import {
  GUEST_UPDATE_FIELD_WHITELIST,
  SERVICE_ROUTED_FIELDS,
} from "./guestFieldWhitelist";
import { hasSuppliedFieldValue } from "@/lib/utils/input-validator";

/**
 * Predicts, fully client-side, how the event's enabled workflows would change a
 * guest right after save — so the admin gets a PREVIEW/WARNING instead of a
 * surprise (a field they typed silently overwritten by an UPDATE_GUEST action).
 *
 * Parity: this mirrors the main-consumer execution semantics exactly. It reuses
 * {@link evaluateConditionGroup} (the verbatim FE↔consumer condition parity
 * port) for trigger conditions and per-action targetFilter, and
 * {@link readGuestUpdates} (the shared canonical/legacy reader) for the field
 * assignments. Limits that can't be replicated client-side (cross-guest target
 * scopes) are surfaced via {@link OverwritePreview.skippedNonTriggering} rather
 * than guessed.
 */

/**
 * Triggers whose effect on ONE guest the browser can reason about: the two
 * explicit ones a cockpit write carries, plus the lifecycle set the backend
 * derives from the guest diff (`deriveLifecycleTriggers`). Anything outside
 * this set (appointments, seating, check-in, scheduled broadcasts) is fired by
 * machinery the form has no snapshot of.
 */
export type GuestTrigger =
  | EmailTrigger.GUEST_CREATED
  | EmailTrigger.GUEST_UPDATED
  | EmailTrigger.REQUEST_SUBMITTED
  | EmailTrigger.GUEST_ACCEPTED
  | EmailTrigger.GUEST_DECLINED
  | EmailTrigger.REQUEST_APPROVED
  | EmailTrigger.REQUEST_DENIED
  | EmailTrigger.COMPANION_APPROVED
  | EmailTrigger.COMPANION_DENIED;

/**
 * Fields the server's condition payload carries but the browser's does not —
 * rollups and provenance built by `GuestPayloadBuilder` /
 * `rawActivityRollups`. A condition touching one of these evaluates against
 * `undefined` here and against a real value in production, so `is_empty` is
 * TRUE client-side and FALSE server-side. We therefore do not trust the
 * verdict: the preview reports `conditionsUncertain` and the caller reconciles
 * instead of predicting.
 */
const ROLLUP_ONLY_FIELDS: ReadonlySet<string> = new Set([
  "apptCount",
  "nextApptAt",
  "lastApptAt",
  "origin",
  "importId",
  "numCompanions",
  "wasWaitlisted",
  "createdAt",
  "updatedAt",
  "speakerId",
  "seatId",
  "seatLabel",
  "lastEmailAt",
  "lastEmailStatus",
  "checkedInAt",
]);

/** True when any condition in the group reads a field the FE payload lacks. */
function touchesRollupField(group: ConditionGroup | null | undefined): boolean {
  if (!group) return false;
  for (const f of group.filters ?? []) {
    const key = String(f.key ?? "");
    // `additionalFields.*` are FE-held; only bare canonical names can be rollups.
    if (ROLLUP_ONLY_FIELDS.has(key)) return true;
  }
  return (group.groups ?? []).some(touchesRollupField);
}

export interface OverwriteEntry {
  /** Canonical/bare field name the action targets (e.g. "lastName", "category"). */
  field: string;
  /** Display label (translated for standard fields, translation/name for custom). */
  label: string;
  isAdditional: boolean;
  /** Resulting value as displayed (option label resolved). Empty string = cleared. */
  toValueDisplay: string;
  /** Raw predicted value — what a reconcile watch compares the server against.
   *  For a delta this is the computed `current + Σdelta`, not the delta. */
  toValueRaw: unknown;
  /** `delta` = the workflow adds to the stored value instead of replacing it. */
  op: "set" | "delta";
  /** Signed, summed delta when `op === "delta"`. */
  deltaAmount?: number;
  /** The server decides this field's outcome (capacity CAS on a lifecycle
   *  column), so the prediction must never be rendered as fact. */
  serverDecided: boolean;
  /** Current form/DB value as displayed. Empty string = currently empty. */
  fromValueDisplay: string;
  /** The user's current value is non-empty AND differs from the workflow value. */
  isConflict: boolean;
  /** Several workflows set this field to DIFFERENT values → server order is
   *  non-deterministic, so the final value is unbestimmt. */
  ambiguous: boolean;
  /** Names of the workflows that set this field. */
  workflowNames: string[];
}

export interface OverwritePreview {
  entries: OverwriteEntry[];
  /** True when at least one matching UPDATE_GUEST action targeted a non-
   *  triggering scope (mainGuest/companions/party) — resolved server-side only,
   *  so it's omitted from the per-field preview and shown as a footnote. */
  skippedNonTriggering: boolean;
  /** A matching workflow's conditions read a field the browser's payload does
   *  not carry, so "does this rule match?" was answered on incomplete data.
   *  `entries` may be wrong in EITHER direction — reconcile, don't trust. */
  conditionsUncertain: boolean;
  /** Human-readable configs the executor will reject at runtime (delta on a
   *  non-numeric or unknown field). They produce no entry: previewing a value
   *  the write never applies is worse than saying nothing. */
  invalidActions: string[];
}

const EMPTY_PREVIEW: OverwritePreview = {
  entries: [],
  skippedNonTriggering: false,
  conditionsUncertain: false,
  invalidActions: [],
};

export interface ComputeArgs {
  workflows: Workflow[];
  fields: Field[];
  /** Would-be-saved guest: flat standard fields + `additionalFields` map.
   *  Build it with {@link buildEvalGuest} so custom-field defaults match what
   *  the server will persist. */
  guest: Record<string, unknown>;
  /** Source for the CURRENT (from-) value shown per field. Defaults to `guest`.
   *  Pass the RAW entered values (pre-default-merge) so a workflow overriding an
   *  untouched custom-field DEFAULT on create reads as a neutral set, not a
   *  conflict against a value the admin never typed. Conditions still use the
   *  merged `guest`. */
  displayGuest?: Record<string, unknown>;
  /** DB snapshot for GUEST_UPDATED (drives changed_to/changed_from); null on create. */
  previousGuest: Record<string, unknown> | null;
  /** Every trigger this one write will fire — the caller's explicit trigger
   *  UNION the lifecycle set derived from the diff. The backend enqueues one
   *  message PER trigger (`GuestService.java:2909-2917`), which is why this is
   *  a set and why a workflow matching two of them runs twice. */
  triggers: GuestTrigger[];
  /** Whether the event has the ACTIONS plugin. The consumer short-circuits the
   *  ENTIRE pipeline without it (`main-consumer/src/lib/workflow-repo.ts:31-40`),
   *  so predicting anything here would warn about writes that cannot happen. */
  actionsPluginEnabled: boolean;
  /** Standard-field label translator (e.g. next-intl "guest-fields"). Should
   *  fall back to the raw name when no translation exists. */
  translateStandard: (name: string) => string;
}

/**
 * Merge custom-field DEFAULT values into the guest object for evaluation/display
 * only. The admin create form does NOT persist untouched defaults — the backend
 * fills them (`GuestService.applyAdditionalFieldDefaults`) — so a workflow
 * condition on a defaulted field must see the default, or the preview diverges
 * from what actually happens. Identical merge to the required-field check.
 * Does NOT mutate the input (callers must not write the merged value back).
 */
export function buildEvalGuest(
  fieldsValues: Record<string, unknown> & { additionalFields?: Record<string, unknown> },
  allFields: Field[],
): Record<string, unknown> {
  const additionalFields: Record<string, unknown> = {
    ...(fieldsValues.additionalFields ?? {}),
  };
  for (const f of allFields) {
    // Blank-aware — see `hasSuppliedFieldValue`. This function exists to
    // PREVIEW what the backend will do, so a presence test here previewed the
    // wrong thing for every blank value.
    if (
      f.additionalField &&
      !hasSuppliedFieldValue(additionalFields, f.name) &&
      f.defaultValue != null
    ) {
      additionalFields[f.name] = f.defaultValue;
    }
  }
  return { ...fieldsValues, additionalFields };
}

function optionLabel(field: Field | undefined, raw: unknown): string {
  const s = String(raw);
  return field?.optionLabels?.[s] ?? s;
}

function displayValue(field: Field | undefined, raw: unknown): string {
  if (raw === null || raw === undefined || raw === "") return "";
  if (Array.isArray(raw)) {
    if (raw.length === 0) return "";
    return raw.map((v) => optionLabel(field, v)).join(", ");
  }
  return optionLabel(field, raw);
}

/**
 * Order-insensitive comparable form used for conflict/no-op/ambiguity checks.
 * Multi-select values are sets, so `["B","A"]` and `["A","B"]` must compare
 * equal — otherwise a mere reordering is mis-flagged as an overwrite conflict.
 * Display still uses {@link displayValue} (stored order) for readability.
 */
function canonicalValue(field: Field | undefined, raw: unknown): string {
  if (raw === null || raw === undefined || raw === "") return "";
  if (Array.isArray(raw)) {
    if (raw.length === 0) return "";
    return raw.map((v) => optionLabel(field, v)).sort().join(", ");
  }
  return optionLabel(field, raw);
}

/** True if the guest's additionalFields map carries this key (used to classify
 *  a workflow target whose Field def isn't loaded — fail toward "additional"
 *  so a real overwrite of a custom field isn't missed). */
function hasAdditionalField(
  guest: Record<string, unknown>,
  fieldName: string,
): boolean {
  const af = guest["additionalFields"];
  return (
    !!af &&
    typeof af === "object" &&
    !Array.isArray(af) &&
    fieldName in (af as Record<string, unknown>)
  );
}

function currentValue(
  guest: Record<string, unknown>,
  fieldName: string,
  isAdditional: boolean,
): unknown {
  if (isAdditional) {
    const af = guest["additionalFields"];
    if (af && typeof af === "object" && !Array.isArray(af)) {
      return (af as Record<string, unknown>)[fieldName];
    }
    return undefined;
  }
  return guest[fieldName];
}

/** Internal accumulator while merging across workflows/actions. */
interface FieldAccum {
  field: Field | undefined;
  /** Last value seen (display) — used as toValue when not ambiguous. */
  lastDisplay: string;
  /** Last value seen (canonical) — used for conflict/no-op comparison. */
  lastCmp: string;
  /** Last RAW value seen — feeds `toValueRaw`. */
  lastRaw: unknown;
  /** Distinct CANONICAL values across pipes → >1 means non-deterministic. */
  distinct: Set<string>;
  names: string[];
  /** Summed signed delta when the field is incremented/decremented. Deltas
   *  COMMUTE, so unlike a `set` they are order-independent — which is exactly
   *  why parallel pipe execution is safe for them and why a delta-only field is
   *  never ambiguous. */
  deltaTotal: number;
  /** Whether any `set` and/or any delta touched this field. Both ⇒ the outcome
   *  really is order-dependent ⇒ ambiguous. */
  sawSet: boolean;
  sawDelta: boolean;
  /** At least one write reads its value off the main guest ⇒ no prediction. */
  sawSourced: boolean;
}

/**
 * Does this guest shape POSITIVELY say it has no main guest?
 *
 * `guestType: "mainGuest"` or an explicitly empty `mainGuest` link. A shape
 * that carries neither key is undecided and counts as "might be a companion" —
 * suppressing a warning on missing evidence is how a real overwrite goes
 * unannounced.
 */
function isKnownMainGuest(guest: Record<string, unknown>): boolean {
  if (guest["guestType"] === "mainGuest") return true;
  if (!Object.prototype.hasOwnProperty.call(guest, "mainGuest")) return false;
  const link = guest["mainGuest"];
  return link === "" || link === null || link === undefined;
}

/** One pipe's net write to one field. */
type PipeWrite =
  | { kind: "set"; value: unknown }
  | { kind: "delta"; amount: number }
  /** Value read off the guest's MAIN GUEST at run time — unknowable here, so
   *  the preview describes the SOURCE instead of inventing a value. */
  | { kind: "sourced"; sourceField: string };

/**
 * Mirror of the gateway's delta refusals
 * (`main-consumer/src/workflows/mongo-guest-update-gateway.ts:116-190`).
 * Returns an error string when the executor would throw, else null.
 */
function deltaRefusal(
  fieldName: string,
  field: Field | undefined,
  amountRaw: unknown,
): string | null {
  if (SERVICE_ROUTED_FIELDS.has(fieldName)) {
    return `cannot increment the non-numeric field "${fieldName}"`;
  }
  if (GUEST_UPDATE_FIELD_WHITELIST.has(fieldName)) {
    // Standard columns are all textual.
    return `cannot increment the non-numeric field "${fieldName}"`;
  }
  if (!field) {
    // No schema row ⇒ the executor cannot verify the target is numeric.
    return `cannot increment "${fieldName}" — no field definition`;
  }
  if (field.dataType !== DataType.LONG) {
    // DOUBLE/CURRENCY stay set-only: a repeated `$inc` accumulates binary
    // floating-point drift, which is unacceptable for money.
    return `cannot increment "${fieldName}" — only whole-number fields support increment/decrement`;
  }
  if (amountRaw === null || amountRaw === undefined || amountRaw === "") {
    return `increment requires a numeric amount for "${fieldName}"`;
  }
  if (!Number.isFinite(Number(amountRaw))) {
    return `increment requires a numeric amount for "${fieldName}"`;
  }
  return null;
}

export function computeWorkflowOverwrites(args: ComputeArgs): OverwritePreview {
  const { workflows, fields, guest, previousGuest, triggers, translateStandard } = args;

  // BW2 parity: without the ACTIONS plugin the consumer never even loads the
  // pipes (`workflow-repo.ts:31-40`), so nothing can run. Fail CLOSED exactly
  // as it does — the consumer treats a missing doc and a non-array `plugins`
  // as false, never as "probably fine".
  if (!args.actionsPluginEnabled) return EMPTY_PREVIEW;
  if (!Array.isArray(triggers) || triggers.length === 0) return EMPTY_PREVIEW;
  const triggerSet = new Set<EmailTrigger>(triggers);

  // Current/from values come from what the admin actually entered; conditions
  // and target filters keep using the defaults-merged `guest`.
  const displayGuest = args.displayGuest ?? guest;
  const acc = new Map<string, FieldAccum>();
  let skippedNonTriggering = false;
  let conditionsUncertain = false;
  const invalidActions: string[] = [];

  const labelOf = (field: Field | undefined, fallbackName: string): string => {
    if (!field) return fallbackName;
    return field.additionalField
      ? field.translation || field.name
      : translateStandard(field.name);
  };

  /**
   * How a value read off the main guest is shown: the localized word for the
   * main guest, then the source field's own label ("Hauptgast: Firma"). No new
   * i18n key — `guest-fields.mainGuest` is the same word the guest list uses
   * for that column, in all 57 locales.
   */
  const sourceDisplay = (sourceField: string): string => {
    const bare = sourceField.startsWith("additionalFields.")
      ? sourceField.slice("additionalFields.".length)
      : sourceField;
    const wantsCustom = sourceField.startsWith("additionalFields.");
    const field = fields.find(
      (f) => f.name === bare && (wantsCustom ? f.additionalField : !f.additionalField),
    );
    return `${translateStandard("mainGuest")}: ${labelOf(field, bare)}`;
  };

  for (const wf of workflows) {
    if (!wf.enabled) continue;
    if (!Array.isArray(wf.triggers)) continue;
    // How many of THIS write's triggers this workflow subscribes to. The
    // backend enqueues one message per trigger, so a workflow matching two of
    // them executes twice. Idempotent for a `set`; a delta genuinely applies
    // twice, and pretending otherwise makes the preview say +5 where
    // production does +10.
    const runCount = wf.triggers.filter((t) => triggerSet.has(t)).length;
    if (runCount === 0) continue;
    if (touchesRollupField(wf.conditions)) conditionsUncertain = true;
    if (!evaluateConditionGroup(wf.conditions, guest, previousGuest)) continue;
    if (!Array.isArray(wf.actions)) continue;

    // UPDATE_GUEST actions in execution order; later action wins within a pipe.
    const actions = wf.actions
      .filter((a) => a.actionType === ActionType.UPDATE_GUEST)
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Resolve this pipe's net write per field. A `set` last-writes-wins within
    // the pipe; deltas accumulate.
    const pipeFieldValue = new Map<string, PipeWrite>();
    for (const a of actions) {
      // Parity with the consumer's `readTargetScope`, which validates against
      // the union and FALLS BACK to "triggering" — an unrecognised value there
      // still writes the triggering guest, so treating it as a skip here would
      // under-predict.
      const scope: TargetScope = readTargetScope(a.config);
      if (scope !== "triggering") {
        skippedNonTriggering = true;
        continue;
      }
      // Parity: the consumer evaluates per-action targetFilter with NO previous
      // snapshot (prev=null), so a filter using changed_to/changed_from never
      // matches server-side. Mirror that here or we over-predict overwrites.
      const targetFilter = a.config?.["targetFilter"] as ConditionGroup | undefined;
      if (targetFilter && !evaluateConditionGroup(targetFilter, guest, null)) {
        continue;
      }
      for (const u of readGuestUpdates(a.config)) {
        if (u.op === "increment" || u.op === "decrement") {
          const field = fields.find((f) => f.name === u.field);
          const refusal = deltaRefusal(u.field, field, u.value);
          if (refusal) {
            if (!invalidActions.includes(refusal)) invalidActions.push(refusal);
            continue;
          }
          const magnitude = Number(u.value);
          const signed = u.op === "decrement" ? -magnitude : magnitude;
          const prev = pipeFieldValue.get(u.field);
          const base = prev?.kind === "delta" ? prev.amount : 0;
          pipeFieldValue.set(u.field, { kind: "delta", amount: base + signed });
          continue;
        }
        if (u.copyFrom) {
          // A guest that HAS no main guest is skipped by the consumer: the
          // column keeps exactly what the admin typed. Predicting an overwrite
          // there put a red conflict row in the save dialog for the majority
          // case and armed a reconcile watch that waits for a change that never
          // comes. Only suppressed on positive evidence — a shape that says
          // nothing about the link keeps its warning.
          if (isKnownMainGuest(guest)) continue;
          // The literal on a sourced row is empty by construction. Recording it
          // as a `set` would make the preview promise "this field will be
          // cleared" — the opposite of what the run does.
          pipeFieldValue.set(u.field, {
            kind: "sourced",
            // A calculation has no source COLUMN to name. The preview says
            // "this value is computed" rather than inventing a field name the
            // organiser would then go looking for.
            sourceField: u.copyFrom.scope === "expr" ? "" : u.copyFrom.field,
          });
          continue;
        }
        pipeFieldValue.set(u.field, { kind: "set", value: u.value });
      }
    }

    for (const [fieldName, write] of pipeFieldValue) {
      const field = fields.find((f) => f.name === fieldName);
      const existing =
        acc.get(fieldName) ??
        ({
          field,
          lastDisplay: "",
          lastCmp: "",
          lastRaw: undefined,
          distinct: new Set<string>(),
          names: [],
          deltaTotal: 0,
          sawSet: false,
          sawDelta: false,
          sawSourced: false,
        } satisfies FieldAccum);

      if (write.kind === "sourced") {
        // Describe the source ("Hauptgast: Firma") rather than predict a value.
        // `distinct` still gets an entry so two workflows writing the field
        // from different sources come out ambiguous, exactly like two literals.
        const sourceLabel = sourceDisplay(write.sourceField);
        existing.lastDisplay = sourceLabel;
        existing.lastCmp = `\u0000sourced:${write.sourceField}`;
        existing.lastRaw = undefined;
        existing.distinct.add(existing.lastCmp);
        existing.sawSet = true;
        existing.sawSourced = true;
      } else if (write.kind === "delta") {
        // ×runCount: the pipe executes once per matching trigger.
        existing.deltaTotal += write.amount * runCount;
        existing.sawDelta = true;
      } else {
        const disp = displayValue(field, write.value);
        const cmp = canonicalValue(field, write.value);
        existing.lastDisplay = disp;
        existing.lastCmp = cmp;
        existing.lastRaw = write.value;
        existing.distinct.add(cmp);
        existing.sawSet = true;
      }
      if (!existing.names.includes(wf.name)) existing.names.push(wf.name);
      acc.set(fieldName, existing);
    }
  }

  const entries: OverwriteEntry[] = [];
  for (const [fieldName, a] of acc) {
    const isAdditional = a.field
      ? (a.field.additionalField ?? false)
      : hasAdditionalField(displayGuest, fieldName);
    const currentRaw = currentValue(displayGuest, fieldName, isAdditional);
    const fromValueDisplay = displayValue(a.field, currentRaw);
    const fromCmp = canonicalValue(a.field, currentRaw);
    // "The value is decided elsewhere, not here" — true for a lifecycle column
    // (capacity CAS) and equally true for a value read off another row.
    const serverDecided = SERVICE_ROUTED_FIELDS.has(fieldName) || a.sawSourced;

    const isDeltaOnly = a.sawDelta && !a.sawSet;
    // A delta-only field is NEVER ambiguous: `$inc`s commute, so parallel pipe
    // execution converges on the same total — and it falls out of the terms
    // rather than needing a special case, since only a `set` ever adds to
    // `distinct`. A field touched by BOTH a set and a delta genuinely is
    // order-dependent, which is the second term.
    const ambiguous = a.distinct.size > 1 || (a.sawDelta && a.sawSet);

    let toValueDisplay: string;
    let toValueRaw: unknown;
    if (isDeltaOnly) {
      // `$inc` on an absent field creates it from 0 (gateway :222-224).
      const base = Number(currentRaw);
      toValueRaw = (Number.isFinite(base) ? base : 0) + a.deltaTotal;
      toValueDisplay = String(toValueRaw);
    } else {
      toValueDisplay = a.lastDisplay;
      toValueRaw = a.lastRaw;
    }

    // No-op: the workflow writes the value the guest already has → not a real
    // change, so don't surface it (otherwise an always-on UPDATE_GUEST nags on
    // every save with a struck-through "X → X"). A zero-sum delta is the same
    // kind of no-op.
    const toCmp = isDeltaOnly ? String(toValueRaw) : a.lastCmp;
    // A sourced write can never be recognised as a no-op — the value it will
    // write is not known here — so it is never dropped by the equality test.
    if (!ambiguous && !a.sawSourced && toCmp === (isDeltaOnly ? String(currentRaw ?? "") : fromCmp)) continue;
    if (isDeltaOnly && a.deltaTotal === 0) continue;

    // A pure increment is not overwriting what the admin typed — flagging it as
    // a conflict would train people to ignore the dialog.
    const isConflict = isDeltaOnly
      ? false
      // A sourced write replaces whatever is there with a value nobody can
      // predict, so a non-empty current value IS the conflict.
      : fromCmp !== "" && (ambiguous || a.sawSourced || fromCmp !== a.lastCmp);

    entries.push({
      field: fieldName,
      label: labelOf(a.field, fieldName),
      isAdditional,
      toValueDisplay,
      toValueRaw,
      op: isDeltaOnly ? "delta" : "set",
      ...(isDeltaOnly ? { deltaAmount: a.deltaTotal } : {}),
      serverDecided,
      fromValueDisplay,
      isConflict,
      ambiguous,
      workflowNames: a.names,
    });
  }

  // Conflicts first, then by label — stable, scannable.
  entries.sort((x, y) => {
    if (x.isConflict !== y.isConflict) return x.isConflict ? -1 : 1;
    return x.label.localeCompare(y.label);
  });

  return { entries, skippedNonTriggering, conditionsUncertain, invalidActions };
}
